import React, { useEffect, useMemo, useState } from "react";
import {
  Box, FormControl, Select, MenuItem, Checkbox, ListItemText,
  Typography, IconButton, CircularProgress,
  FormHelperText
} from "@mui/material";
import { TrashIcon } from "../../icons";
import AccreditationNAAC from "./AccreditationNAAC";
import AccreditationNIRF from "./AccreditationNIRF";
import AccreditationDefault from "./AccreditationDefault";
import UploadCertificate from "./UploadCertificate";
import { fileUpload, fileDelete } from "../../api/uploadApi";
import { fetchAccreditationList } from "../../api/accreditationApi";
import ValidationErrorText from "../common/ValidationErrorText";

const AccreditationForm = ({ value = [], onChange, readOnly = false,fieldError=[] }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [busy, setBusy] = useState({}); // { [accreditationId]: { uploading:bool, deleting:bool } }

  useEffect(() => {
    (async () => {
      try {
        await fetchAccreditationList(setOptions, setLoading); // expects [{value,label,code?,logoUrl?}]
      } catch {
        setFetchError("Failed to fetch accreditation options.");
        setLoading(false);
      }
    })();
  }, []);

  const byId = useMemo(() => new Map(options.map(o => [o.value, o])), [options]);
  const selectedIds = useMemo(() => value.map(v => v.accreditationId), [value]);
  const emit = (next) => onChange && onChange(next);
  const handleSelectChange = (e) => {
    const nextIds = typeof e.target.value === "string"
      ? e.target.value.split(",")
      : e.target.value;

    const existing = new Set(selectedIds);
    const addedEntries = nextIds
      .filter(id => !existing.has(id))
      .map(id => ({ accreditationId: id, data: {}, certificate: "" }));

    const kept = value.filter(v => nextIds.includes(v.accreditationId));

    emit([...kept, ...addedEntries]);
  };

  const removeOne = (accreditationId) => {
    emit(value.filter(v => v.accreditationId !== accreditationId));
  };

  const patchEntry = (accreditationId, patch) => {
    emit(value.map(v => v.accreditationId === accreditationId ? { ...v, ...patch } : v));
  };

  const patchData = (accreditationId, dataPatch) => {
    emit(value.map(v =>
      v.accreditationId === accreditationId
        ? { ...v, data: { ...(v.data || {}), ...dataPatch } }
        : v
    ));
  };

  const uploadFile = async (file, accreditationId) => {
    if (!file) return;
    // Allowed MIME types
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/jpg", "application/pdf"];

    // Validate file type
    if (!allowedTypes.includes(file.type)) {
      alert("Only image files (jpg, jpeg, png, webp) or PDF files are allowed.");
      return;
    }
    setBusy(prev => ({ ...prev, [accreditationId]: { ...(prev[accreditationId]||{}), uploading: true }}));
    try {
      const uploaded = await fileUpload(file);
      patchEntry(accreditationId, { certificate: uploaded.url || uploaded.path || "" });
    } catch {
      alert("File upload failed.");
    } finally {
      setBusy(prev => ({ ...prev, [accreditationId]: { ...(prev[accreditationId]||{}), uploading: false }}));
    }
  };

  const removeFile = async (accreditationId) => {
    const entry = value.find(v => v.accreditationId === accreditationId);
    if (!entry?.certificate) return;

    setBusy(prev => ({ ...prev, [accreditationId]: { ...(prev[accreditationId]||{}), deleting: true }}));
    try {
      await fileDelete(entry.certificate);
      patchEntry(accreditationId, { certificate: "" });
    } catch {
      alert("Delete failed.");
    } finally {
      setBusy(prev => ({ ...prev, [accreditationId]: { ...(prev[accreditationId]||{}), deleting: false }}));
    }
  };

  const renderRow = (entry) => {
    const opt = byId.get(entry.accreditationId);
    if (!opt) return null;

    const uploadSection = (
      <UploadCertificate
        option={opt}
        file={entry.certificate ? { url: entry.certificate } : null}
        uploading={!!busy[entry.accreditationId]?.uploading}
        deleting={!!busy[entry.accreditationId]?.deleting}
        onUpload={(e) => uploadFile(e.target.files?.[0], entry.accreditationId)}
        onRemove={() => removeFile(entry.accreditationId)}
        readOnly={readOnly}
      />
    );

    if (opt.label?.startsWith("NAAC")) {
      return (
        <AccreditationNAAC
          option={opt}
          data={entry.data || {}}
          onChange={(partial) => patchData(entry.accreditationId, partial)}
          renderUploadSection={() => uploadSection}
          readOnly={readOnly}
        />
      );
    }
    if (opt.label?.startsWith("NIRF")) {
      return (
        <AccreditationNIRF
          option={opt}
          data={entry.data || {}}
          onChange={(partial) => patchData(entry.accreditationId, partial)}
          renderUploadSection={() => uploadSection}
          readOnly={readOnly}
        />
      );
    }
    return (
      <AccreditationDefault
        option={opt}
        data={entry.data || {}}
        onChange={(partial) => patchData(entry.accreditationId, partial)}
        renderUploadSection={() => uploadSection}
        readOnly={readOnly}
      />
    );
  };

  return (
    <Box>
      <Typography mb={1}>Accreditation Body</Typography>

      {loading ? (
        <Box display="flex" alignItems="center" py={2} gap={2}>
          <CircularProgress size={20} /> <span>Loading options...</span>
        </Box>
      ) : fetchError ? (
        <Typography color="error" fontSize={13}>{fetchError}</Typography>
      ) : (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <Select
            multiple
            value={selectedIds}
            onChange={handleSelectChange}
            displayEmpty
            disabled={readOnly}
            renderValue={(sel) => {
              const ids = Array.isArray(sel) ? sel : [];
              if (!ids.length) return "Select Accreditation";
              const s = ids.map(id => byId.get(id)?.label || "").filter(Boolean).join(", ");
              return s.length > 40 ? `${s.slice(0, 40)}…` : s;
            }}
            sx={{ height: 38, fontSize: 13 }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
          >
            <MenuItem disabled value="" sx={{ fontSize: 13 }}>
              <em>Select Accreditation</em>
            </MenuItem>
            {options.map(opt => (
              <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>
                <Checkbox checked={selectedIds.includes(opt.value)} sx={{ py: 0 }} />
                <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: 13 }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {value.map((entry,index) => (
        <Box
          key={entry.accreditationId}
          sx={{ mt: 0, p: 2, position: "relative", backgroundColor: "#fff", borderTop: "1px solid #ccc" }}
        >
          {!readOnly && (
            <IconButton
              size="small"
              onClick={() => removeOne(entry.accreditationId)}
              sx={{ position: "absolute", top: 8, right: 8 }}
            >
              <TrashIcon />
            </IconButton>
          )}
          {renderRow(entry)}
          <ValidationErrorText error={fieldError?.[index]}/>
        </Box>
      ))}
    </Box>
  );
};

export default AccreditationForm;
